import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, Shield, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StockCheckLogo } from "@/components/Logo";
import api from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSetupMode, setIsSetupMode] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // Check if admin exists on component mount
  useEffect(() => {
    // If already logged in as admin, redirect
    const adminToken = localStorage.getItem("adminToken");
    if (adminToken) {
      navigate("/admin");
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Try regular admin login first
      const response = await api.post("/admin/login", formData);
      localStorage.setItem("adminToken", response.data.token);
      localStorage.setItem("admin", JSON.stringify(response.data.admin));
      toast.success("Welcome, Admin!");
      navigate("/admin");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Login failed";
      
      // If the error is "You do not have admin privileges", suggest setup
      if (errorMsg.includes("do not have admin privileges")) {
        toast.error("You don't have admin privileges. Contact an existing admin.");
      } else if (errorMsg.includes("Invalid credentials")) {
        toast.error("Invalid email or password");
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post("/admin/setup-first-admin", formData);
      localStorage.setItem("adminToken", response.data.token);
      localStorage.setItem("admin", JSON.stringify(response.data.admin));
      toast.success("Admin account created! Welcome!");
      navigate("/admin");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Setup failed";
      
      if (errorMsg.includes("Admin user already exists")) {
        toast.error("An admin already exists. Use regular login.");
        setIsSetupMode(false);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center p-4">
      <div className="grid-background absolute inset-0 pointer-events-none opacity-30" />
      
      <div className="relative w-full max-w-md">
        <div className="bg-[#151923] border border-slate-800 rounded-lg p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <StockCheckLogo size="large" showText={true} />
          </div>

          <div className="flex items-center justify-center gap-2 mb-6">
            <Shield className="w-6 h-6 text-red-500" />
            <h1 className="text-xl font-bold text-white">Admin Portal</h1>
          </div>

          <p className="text-slate-400 text-center mb-8 text-sm">
            {isSetupMode 
              ? "Create your first admin account" 
              : "Authorized personnel only"
            }
          </p>

          {/* Admin Login/Setup Form */}
          <form onSubmit={isSetupMode ? handleSetup : handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-300">
                {isSetupMode ? "Admin Email" : "Email"}
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="admin@yourcompany.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="admin-email-input"
                  className="pl-10 bg-[#0B0E14] border-slate-700 text-white"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={isSetupMode ? 8 : undefined}
                  data-testid="admin-password-input"
                  className="pl-10 pr-10 bg-[#0B0E14] border-slate-700 text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {isSetupMode && (
                <p className="text-slate-500 text-xs mt-1">Minimum 8 characters</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="w-full bg-red-600 hover:bg-red-700"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  {isSetupMode ? "Creating..." : "Verifying..."}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isSetupMode ? <UserPlus className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                  {isSetupMode ? "Create Admin Account" : "Access Admin Dashboard"}
                </span>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-[#151923] text-slate-500">or</span>
            </div>
          </div>

          {/* Toggle between login and setup */}
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsSetupMode(!isSetupMode)}
            className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
            data-testid="toggle-setup-mode"
          >
            {isSetupMode ? (
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Already have admin access? Sign in
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                First time? Set up admin account
              </span>
            )}
          </Button>

          <p className="mt-4 text-center text-slate-500 text-xs">
            {isSetupMode 
              ? "This will only work if no admin exists yet." 
              : "This area is restricted to administrators only."
            }
          </p>
        </div>
      </div>
    </div>
  );
}
